import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import type { OrderBlock } from "@shared/lessonBlocks";
import { ArrowUp, ArrowDown } from "lucide-react";
import { RichText } from "../RichText";
import { useCheck, CheckFooter, shuffled, type CheckResult } from "../CheckShell";

interface Props {
  block: OrderBlock;
  onResult: (r: CheckResult) => void;
  alreadyDone?: CheckResult | null;
}

/** Arrange items with up/down arrows, or tap two items to swap them. */
export function OrderBlockView({ block, onResult, alreadyDone }: Props) {
  const check = useCheck(onResult, { alreadyDone });
  const initial = useMemo(() => shuffled(block.items.map(i => i.id)), [block.id]);
  const [order, setOrder] = useState<string[]>(initial);
  const [selected, setSelected] = useState<string | null>(null);
  const [wrongPositions, setWrongPositions] = useState<Set<number>>(new Set());
  const done = check.locked;
  const correctOrder = block.items.map(i => i.id);
  const textOf = (id: string) => block.items.find(i => i.id === id)?.text || "";

  const mutate = (fn: (o: string[]) => string[]) => {
    if (done) return;
    if (check.status === "wrong") check.retry();
    setWrongPositions(new Set());
    setOrder(o => fn(o));
  };
  const move = (idx: number, dir: -1 | 1) => mutate(o => {
    const j = idx + dir;
    if (j < 0 || j >= o.length) return o;
    const n = [...o];
    [n[idx], n[j]] = [n[j], n[idx]];
    return n;
  });
  const tap = (id: string) => {
    if (done) return;
    if (!selected) { setSelected(id); return; }
    if (selected === id) { setSelected(null); return; }
    mutate(o => {
      const a = o.indexOf(selected), b = o.indexOf(id);
      const n = [...o];
      [n[a], n[b]] = [n[b], n[a]];
      return n;
    });
    setSelected(null);
  };
  const submit = () => {
    const wrong = new Set<number>();
    order.forEach((id, i) => { if (id !== correctOrder[i]) wrong.add(i); });
    setWrongPositions(wrong);
    check.submit(wrong.size === 0);
  };
  const shown = done && check.status === "revealed" ? correctOrder : order;

  return (
    <div className="space-y-4">
      <RichText text={block.prompt} className="text-lg md:text-xl font-semibold" />
      <p className="text-sm text-muted-foreground">Use the arrows, or tap two items to swap them.</p>
      <ol className="space-y-2">
        {shown.map((id, idx) => {
          const isSel = selected === id;
          const isWrong = wrongPositions.has(idx) && !done;
          const cls = done
            ? (check.status === "correct" || id === correctOrder[idx] ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30" : "border-rose-400 bg-rose-50 dark:bg-rose-900/30")
            : isWrong ? "border-rose-400 bg-rose-50 dark:bg-rose-900/30"
            : isSel ? "border-primary bg-primary/10 ring-2 ring-primary/30"
            : "border-border bg-card";
          return (
            <motion.li layout key={id} className={`flex items-center gap-2 rounded-2xl border-2 p-2 ${cls}`} data-testid={`order-item-${id}`}>
              <span className="w-8 h-8 rounded-full bg-muted font-bold text-sm flex items-center justify-center flex-shrink-0">{idx + 1}</span>
              <button type="button" disabled={done} onClick={() => tap(id)} className="flex-1 text-left py-1.5 px-1 font-medium">
                <RichText text={textOf(id)} inline />
              </button>
              {!done && (
                <div className="flex flex-col gap-0.5">
                  <button type="button" aria-label="Move up" disabled={idx === 0} onClick={() => move(idx, -1)} className="p-1 rounded-md hover:bg-muted disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                  <button type="button" aria-label="Move down" disabled={idx === shown.length - 1} onClick={() => move(idx, 1)} className="p-1 rounded-md hover:bg-muted disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                </div>
              )}
            </motion.li>
          );
        })}
      </ol>
      <CheckFooter
        check={check}
        canSubmit={true}
        onSubmit={submit}
        feedbackWrong={wrongPositions.size ? `${wrongPositions.size} ${wrongPositions.size === 1 ? "item is" : "items are"} in the wrong spot.` : undefined}
        revealAnswer={<p>The list is now in the correct order.</p>}
      />
    </div>
  );
}
